import { classify } from 'sidecar-utilities'

import { Sentiment } from '@sidecar-components/types'

export const rowStyles = ({
  borderOverflow,
  hasFooter,
  internalLink,
  link,
  onClick,
  sentiment = Sentiment.DEFAULT,
  totalRow,
}: {
  borderOverflow?: boolean
  hasFooter?: boolean
  sentiment?: Sentiment
  totalRow?: boolean
  link?: boolean
  internalLink?: boolean
  onClick?: () => void
}) => {
  // This switch is for handling the variations with sentiment.
  // Because tailwind needs the whole string, we need to prefix with
  // `before:` and `after:` where applicable.
  const rowSentiments = {
    hoverColor: 'hover:color-bgDefaultContainer-hover',
    activeColor: 'active:color-bgDefaultContainer-active',
    focusColor: 'focus:color-bgDefaultContainer-hover',
    borderColor:
      'before:color-fauxBorder-weak-default after:color-fauxBorder-weak-default',
  }
  switch (sentiment) {
    case Sentiment.NOTIFY:
      rowSentiments.hoverColor = 'hover:color-background-notify-hover'
      rowSentiments.activeColor = 'active:color-background-notify-active'
      rowSentiments.focusColor = 'focus:color-background-notify-hover'
      rowSentiments.borderColor =
        'before:color-fauxBorderNotify-default after:color-fauxBorderNotify-default'
      break
    case Sentiment.ACCENT:
      rowSentiments.hoverColor = 'hover:color-background-accent-hover'
      rowSentiments.activeColor = 'active:color-background-accent-active'
      rowSentiments.focusColor = 'focus:color-background-accent-hover'
      rowSentiments.borderColor =
        'before:color-fauxBorderAccent-weak-default after:color-fauxBorderAccent-weak-default'
      break
    case Sentiment.RISK:
      rowSentiments.hoverColor = 'hover:color-background-risk-hover'
      rowSentiments.activeColor = 'active:color-background-risk-active'
      rowSentiments.focusColor = 'hover:color-background-risk-hover'
      rowSentiments.borderColor =
        'before:color-fauxBorderRisk-weak-default after:color-fauxBorderRisk-weak-default'
      break
    case Sentiment.BRAND:
      rowSentiments.hoverColor = 'hover:color-layerOnBrand-hover'
      rowSentiments.activeColor = 'active:color-layerOnBrand-active'
      rowSentiments.focusColor = 'focus:color-layerOnBrand-hover'
      rowSentiments.borderColor =
        'before:color-fauxBorderAccent-weak-default after:color-fauxBorderAccent-weak-default'
      break
    // no default
  }
  return classify({
    // The 'row' class allows the <Rows> component to select this div
    parent: 'group/row row',
    // Colours only: left to the browser default of `all`, a footer appearing
    // under the row animated its padding change as a visible jump.
    transition: 'transition-colors ease-in duration-150',
    layout: 'flex items-center my-0 py-0',
    // With a footer the card wrapper is the list's child, so it owns the
    // negative margins that pull the first and last rows into the card padding.
    firstAndLast: !hasFooter && 'first:-mt-3 last:-mb-3',
    // negative margin so its padding doesn't effect its parent's padding
    mobile: borderOverflow
      ? 'w-[calc(100%+theme(space.10))] px-5 -mx-5 gap-4'
      : 'w-full gap-3',
    tablet:
      !!borderOverflow && 'sm:w-[calc(100%+theme(space.12))] sm:px-6 sm:-mx-6',
    desktop:
      !!borderOverflow && 'lg:w-[calc(100%+theme(space.16))] lg:px-8 lg:-mx-8',
    // If a total row, add an extra border element so it looks like a
    // "totals" row (for expense math)
    // This element is displayed on top of the row above it
    // but is needed when the <Row> is showBorders=false
    totalRow:
      !!totalRow &&
      `relative ${rowSentiments.borderColor} mt-0.5 
          before:h-px before:absolute before:top-0 before:right-0
          after:h-px after:absolute after:-top-[3px] after:right-0`,
    // If a total row, add an extra border element so it looks like a
    // "totals" row (for expense math)
    totalRowExtraBorderOverflow: totalRow
      ? borderOverflow
        ? `before:left-5 sm:before:left-6 lg:before:left-8
            after:left-5 sm:after:left-6 lg:after:left-8`
        : `before:left-0 after:left-0`
      : null,
    // Remove the double border if the total row is the first child. With a
    // footer the row is always the first child of its card, so `first:` would
    // match every time and suppress the border unconditionally — ask the card
    // whether it is first instead, the same way the footer asks whether it is
    // last.
    firstTotalRow:
      !!totalRow &&
      (hasFooter
        ? `group-first/rowCard:before:content-none
           group-first/rowCard:after:content-none`
        : 'first:before:content-none first:after:content-none'),
    // if the row is clickable (vs only a read-only or has a child button),
    // the whole row should have a hover/active coloring
    linkEffects:
      (link || onClick || internalLink) &&
      `${rowSentiments.hoverColor} ${rowSentiments.activeColor} ${rowSentiments.focusColor} cursor-pointer`,
  })
}

/**
 * The bottom-border colors for a row's separator, shared by the row body and
 * the footer slot so a footer's border matches the body border it replaces.
 */
const separatorColors = (sentiment: Sentiment) => {
  // This switch is for handling the variations with sentiment.
  // Because tailwind needs the whole string, we need to prefix with
  // `after:` where applicable.
  const rowBodySentiments = {
    borderColor: 'color-border-weak-default',
    afterBorderColor: 'after:color-fauxBorder-weak-default',
  }
  switch (sentiment) {
    case Sentiment.NOTIFY:
      rowBodySentiments.borderColor = 'color-borderNotify-default'
      rowBodySentiments.afterBorderColor =
        'after:color-fauxBorderNotify-default'
      break
    case Sentiment.ACCENT:
      rowBodySentiments.borderColor = 'color-borderAccent-weak-default'
      rowBodySentiments.afterBorderColor =
        'after:color-fauxBorderAccent-weak-default'
      break
    case Sentiment.RISK:
      rowBodySentiments.borderColor = 'color-borderRisk-weak-default'
      rowBodySentiments.afterBorderColor =
        'after:color-fauxBorderRisk-weak-default'
      break
    case Sentiment.BRAND:
      rowBodySentiments.borderColor = 'color-borderAccent-default'
      rowBodySentiments.afterBorderColor =
        'after:color-fauxBorderAccent-default'
      break
    // no default
  }
  return rowBodySentiments
}

/**
 * The border-overflow sliver: a 1px bar in the bottom-right corner that carries
 * the separator out past the card's own padding, so the row's container can
 * keep normal padding.
 */
const overflowSliver = (afterBorderColor: string) =>
  `${afterBorderColor}
            after:absolute after:-bottom-px after:-right-5 after:w-5 after:h-px
            sm:after:-right-6 sm:after:w-6
            lg:after:-right-8 lg:after:w-8`

export const rowBodyStyles = ({
  borderOverflow,
  hasFooter,
  sentiment = Sentiment.DEFAULT,
}: {
  borderOverflow?: boolean
  hasFooter?: boolean
  sentiment?: Sentiment
}) => {
  const rowBodySentiments = separatorColors(sentiment)

  return classify({
    // With a footer, the separator moves to the footer — the row's own bottom
    // border would otherwise draw a line between the row and its footer, which
    // is meant to read as one card.
    border: hasFooter
      ? 'border-b-0'
      : `border-b ${rowBodySentiments.borderColor}`,
    borderOverflow:
      !!borderOverflow &&
      !hasFooter &&
      overflowSliver(rowBodySentiments.afterBorderColor),
    layout: 'w-full flex relative self-stretch items-center',
    // With a footer, the card's bottom padding belongs below the footer, not
    // between the row and it — otherwise a footer reads as detached content
    // rather than part of the same row.
    py: hasFooter ? 'pt-3 pb-0' : 'py-3',
    // If no borderOverflow, we need a little extra x padding for the hover state
    px: borderOverflow ? 'px-0' : 'px-1',
    chevronSpace: 'gap-2',
    // lastChild removes the border and padding from the last row
    // so it doesn't duplicate its parent padding.
    lastChild:
      !hasFooter && 'group-last/row:border-0 group-last/row:after:content-none',
  })
}

/**
 * The element wrapping a row and its footer. Only rendered when a footer is
 * passed, and it becomes the list's child in the row's place — so it takes over
 * the first/last negative margins, and owns `group/rowCard` for the footer's
 * "last card drops its separator" rule.
 */
export const rowCardStyles = () =>
  classify({
    parent: 'group/rowCard',
    firstAndLast: 'first:-mt-3 last:-mb-3',
    // The row inside is `w-[calc(100%+space.10)] -mx-5`, which needs a parent
    // with a width to be 100% of. `Rows` is a block, so this is moot there — but
    // a footer-bearing row dropped straight into a `flex-col items-start` parent
    // would shrink-wrap this wrapper and the row would measure itself against
    // the collapsed box.
    width: 'w-full',
  })

/**
 * The footer slot: inside the row's visual card but outside its clickable
 * element, so it can hold its own controls. It draws the separator the row body
 * gives up, on the same rules — including dropping it on the last card.
 */
export const rowFooterStyles = ({
  borderOverflow,
  sentiment = Sentiment.DEFAULT,
}: {
  borderOverflow?: boolean
  sentiment?: Sentiment
}) => {
  const rowBodySentiments = separatorColors(sentiment)

  return classify({
    // `rowFooter` is a hook, not a style: this element holds the separator that
    // used to live inside `.row`, so `Rows`' showBorders={false} suppression has
    // to be able to reach it here too.
    parent: 'rowFooter',
    border: `border-b ${rowBodySentiments.borderColor}`,
    borderOverflow:
      !!borderOverflow && overflowSliver(rowBodySentiments.afterBorderColor),
    layout: 'relative',
    // The bottom padding the row body handed over, so a row with a footer has
    // the same overall height and the same gap to the border as one without.
    // Top spacing is left to the consumer — how close the footer sits to the row
    // is what says the two belong together.
    py: 'pb-3',
    lastChild:
      'group-last/rowCard:border-0 group-last/rowCard:after:content-none',
  })
}

export const leftMediaStyles = borderOverflow =>
  classify({
    ySpacing: 'py-3',
    // If no borderOverflow, add 4px to the left of the media so on hover, the media doesn't touch the edge of the hover bg
    xSpacing: borderOverflow ? null : 'pl-1',
  })

export const chevronStyles = ({
  sentiment = Sentiment.DEFAULT,
}: {
  sentiment?: Sentiment
}) => {
  // This switch is for handling the variations with sentiment.
  // Because tailwind needs the whole string, we need to prefix with
  // `before:` and `after:` where applicable.
  const chevronSentiments = {
    chevronColor: 'color-icon-weak-default',
    chevronHoverColor: 'group-hover/row:color-icon-weak-hover',
    chevronActiveColor: 'group-active/row:color-icon-weak-active',
    chevronFocusColor: 'group-focus/row:color-icon-weak-hover',
  }
  switch (sentiment) {
    case Sentiment.NOTIFY:
      chevronSentiments.chevronColor = 'color-iconOnNotify-bold-default'
      chevronSentiments.chevronHoverColor =
        'group-hover/row:color-iconOnNotify-bold-default'
      chevronSentiments.chevronActiveColor =
        'group-active/row:color-iconOnNotify-bold-default'
      chevronSentiments.chevronFocusColor =
        'group-focus/row:color-iconOnNotify-bold-default'
      break
    case Sentiment.ACCENT:
      chevronSentiments.chevronColor = 'color-iconOnAccent-weak-default'
      chevronSentiments.chevronHoverColor =
        'group-hover/row:color-iconOnAccent-weak-default'
      chevronSentiments.chevronActiveColor =
        'group-active/row:color-iconOnAccent-weak-default'
      chevronSentiments.chevronFocusColor =
        'group-focus/row:color-iconOnAccent-weak-default'
      break
    case Sentiment.RISK:
      chevronSentiments.chevronColor = 'color-iconOnRisk-weak-default'
      chevronSentiments.chevronHoverColor =
        'group-hover/row:color-iconOnRisk-weak-default'
      chevronSentiments.chevronActiveColor =
        'group-active/row:color-iconOnRisk-weak-default'
      chevronSentiments.chevronFocusColor =
        'group-focus/row:color-iconOnRisk-weak-default'
      break
    case Sentiment.BRAND:
      chevronSentiments.chevronColor = 'color-iconOnBrand-weak-default'
      chevronSentiments.chevronHoverColor =
        'group-hover/row:color-iconOnBrand-weak-default'
      chevronSentiments.chevronActiveColor =
        'group-active/row:color-iconOnBrand-weak-default'
      chevronSentiments.chevronFocusColor =
        'group-focus/row:color-iconOnBrand-weak-default'
      break
    // no default
  }
  return classify(chevronSentiments)
}

export const childrenStyles = ({
  sentiment = Sentiment.DEFAULT,
}: {
  sentiment?: Sentiment
}) => {
  // This switch is for handling the variations with sentiment.
  // Because tailwind needs the whole string, we need to prefix with
  // `before:` and `after:` where applicable.
  const childrenSentiments = {
    textColor: 'color-textOnDefault',
    hoverColor: 'group-hover/row:color-text-hover',
    activeColor: 'group-active/row:color-text-active',
    focusColor: 'group-focus/row:color-text-hover',
    childrenColor:
      '[&.inherit-link-color]:color-textPrimary [&_.inherit-link-color]:color-textPrimary',
    childrenHoverColor:
      '[&.inherit-link-color]:group-hover/row:color-textPrimary-hover [&_.inherit-link-color]:group-hover/row:color-textPrimary-hover',
    childrenActiveColor:
      '[&.inherit-link-color]:group-active/row:color-textPrimary-active [&_.inherit-link-color]:group-active/row:color-textPrimary-active',
    childrenFocusColor:
      '[&.inherit-link-color]:group-focus/row:color-textPrimary-hover [&_.inherit-link-color]:group-focus/row:color-textPrimary-hover',
  }
  switch (sentiment) {
    case Sentiment.NOTIFY:
      childrenSentiments.textColor = 'color-textOnNotify-default'
      childrenSentiments.hoverColor = 'group-hover/row:color-textOnNotify-hover'
      childrenSentiments.activeColor =
        'group-active/row:color-textOnNotify-active'
      childrenSentiments.focusColor = 'group-focus/row:color-textOnNotify-hover'
      break
    case Sentiment.ACCENT:
      childrenSentiments.textColor = 'color-textOnAccent-default'
      childrenSentiments.hoverColor = 'group-hover/row:color-textOnAccent-hover'
      childrenSentiments.activeColor =
        'group-active/row:color-textOnAccent-active'
      childrenSentiments.focusColor = 'group-focus/row:color-textOnAccent-hover'
      break
    case Sentiment.RISK:
      childrenSentiments.textColor = 'color-textOnRisk-default'
      childrenSentiments.hoverColor = 'group-hover/row:color-textOnRisk-hover'
      childrenSentiments.activeColor =
        'group-active/row:color-textOnRisk-active'
      childrenSentiments.focusColor = 'group-focus/row:color-textOnRisk-hover'
      break
    case Sentiment.BRAND:
      childrenSentiments.textColor = 'color-textOnBrand-default'
      childrenSentiments.hoverColor = 'group-hover/row:color-textOnBrand-hover'
      childrenSentiments.activeColor =
        'group-active/row:color-textOnBrand-active'
      childrenSentiments.focusColor = 'group-focus/row:color-textOnBrand-hover'
      childrenSentiments.childrenColor =
        '[&.inherit-link-color]:color-textEngageOnBrand-default [&_.inherit-link-color]:color-textEngageOnBrand-default'
      childrenSentiments.childrenHoverColor =
        '[&.inherit-link-color]:group-hover/row:color-textEngageOnBrand-hover [&_.inherit-link-color]:group-hover/row:color-textEngageOnBrand-hover'
      childrenSentiments.childrenActiveColor =
        '[&.inherit-link-color]:group-active/row:color-textEngageOnBrand-active [&_.inherit-link-color]:group-active/row:color-textEngageOnBrand-active'
      childrenSentiments.childrenFocusColor =
        '[&.inherit-link-color]:group-focus/row:color-textEngageOnBrand-hover [&_.inherit-link-color]:group-focus/row:color-textEngageOnBrand-hover'
      break
  }
  return classify(childrenSentiments)
}
